import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  makeStyles,
  withStyles,
  Slider,
  Popover,
  Drawer,
  Divider,
} from '@material-ui/core';
import {
  Close,
  ExpandLess,
  ExpandMore,
  Forward10,
  PauseCircleOutline,
  PlayCircleOutline,
  Replay10,
  VolumeDown,
  VolumeUp,
  Settings,
} from '@material-ui/icons';

import { SCREEN_SIZE_CATEGORIES, TEXT_TO_SPEECH } from 'constants';
import {
  AnalyticsModifiersContext,
  ViewportValuesContext,
  BookLocationDocumentValuesContext,
} from 'contexts';
import IconButton from 'components/IconButton';
import { IconTooltip } from 'components';
import { TTSSpeed, TTSVoiceControl, TTSAutoFitControl } from '..';
import { Typography } from '@redshelf/redshelf-ui';

const useStyles = makeStyles((theme) => ({
  ttscontrols: {
    visibility: ({ visible }) => (visible ? 'visible' : 'hidden'),
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    alignItems: 'center',
    position: 'relative',
    justifyContent: 'center',
    borderTop: 'solid 1px #E5E5E5',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-around',
    },
    backgroundColor: '#fff',
    marginBottom: ({ visible }) => (visible ? '0px' : '-51px'),
  },
  ttsIconButton: {
    padding: theme.spacing(1.25),
    minWidth: 'auto',
  },
  playBtn: {
    padding: theme.spacing(0.5),
    minWidth: 'auto',
  },
  playBtnIcon: {
    fontSize: '2.25rem',
  },
  closeBtn: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flex: '0 1 auto',
      marginRight: '12px',
    },
  },

  ttscontrols__volume: {
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    height: theme.spacing(5.5), // 44px
    [theme.breakpoints.down('sm')]: {
      flexGrow: 1,
      flexBasis: '100%',
      justifyContent: 'center',
      maxWidth: '100%',
    },
  },
  hiddenSliderTooltipContainer: {
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: '1px',
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  },

  ttscontrols__reproduction: {
    display: 'flex',
    alignItems: 'center',
    padding: '0px 32px',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },

  formControlSelect: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#767676',
    },
    '& .MuiOutlinedInput-root': {
      margin: '4px',
    },
  },
  voiceSelect: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexGrow: 2,
    },
  },
  rateSelect: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexGrow: 1,
    },
  },

  mobileTTScontrols: ({ visible }) => ({
    visibility: visible ? 'visible' : 'hidden',
    marginBottom: visible ? '0px' : '-51px',
    borderTop: 'solid 1px #E5E5E5',
    backgroundColor: '#fff',
    [theme.breakpoints.down('xs')]: {
      boxShadow:
        '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
    },
  }),
  mobileAccordion: {
    display: 'flex',
    justifyContent: 'space-between',
    height: theme.spacing(5.5), // 44px
  },
  mobileAccordionButton: {
    marginLeft: '12px',
  },
  accordionContents: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-evenly',
    alignContent: 'inherit',
    padding: theme.spacing(0, 1.125, 1.75, 1.125), // 0 9px 14px 9px
    height: theme.spacing(7.5), // 60px
  },

  controlsContainer: {
    height: theme.spacing(5.5), // 44px
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    gap: theme.spacing(7.5),
  },
  controlsCloseButtonContainer: {
    display: 'flex',
    marginRight: '12px',
  },
  voiceControls: {
    minHeight: theme.spacing(34.25),
    minWidth: theme.spacing(40),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    ...theme.typography.h6,
    gap: theme.spacing(0.5),
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 1, 2), // 8px 8px 8px 16px
  },
  controls: {
    minHeight: theme.spacing(26.25),
    padding: theme.spacing(2, 1),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  ttsSettingsButton: ({ isTTSSettingsOpen }) => ({
    backgroundColor: isTTSSettingsOpen
      ? theme.palette.shelfBlue[50]
      : 'transparent',
  }),
  ttsSettingIconContainer: {
    width: theme.spacing(28.125),
  },
}));

const VolumeSlider = withStyles((theme) => {
  return {
    root: {
      width: 171,
      height: theme.spacing(5.5), // 44px
      padding: '22px 0',
      boxSizing: 'inherit',
      [theme.breakpoints.down('sm')]: {
        width: '88%',
      },
      '&:hover $thumb': {
        backgroundColor: theme.palette.shelfBlue['600'],
      },
      '&:hover $track': {
        backgroundColor: theme.palette.shelfBlue['500'],
      },
      '&:hover $rail': {
        backgroundColor: theme.palette.shelfBlue['50'],
      },
      '&:hover $mark': {
        backgroundColor: theme.palette.shelfBlue['50'],
        opacity: 1,
      },
      '&:hover .markLabel': {
        opacity: 1,
      },
      minHeight: theme.spacing(0.5), // 4px, so 20px + 4px + 20px === 4px 🎉
    },
    thumb: {
      backgroundColor: theme.palette.greyBlue['700'],
      transition: 'backgroundColor 0.2s',
    },
    valueLabel: {
      fontSize: 14,
    },
    track: {
      height: 2,
      borderRadius: 0,
      backgroundColor: theme.palette.greyBlue['700'],
      transition: 'backgroundColor 0.2s',
    },
    rail: {
      height: 2,
      backgroundColor: theme.palette.greyBlue['100'],
      borderRadius: 1,
      transition: 'backgroundColor 0.2s',
    },
    mark: {
      backgroundColor: '#fafafa',
      transition: 'all 0.2s',
    },
    markActive: {
      backgroundColor: theme.palette.grey,
    },
  };
})(Slider);

const CloseButton = ({ classes, onClose }) => {
  return (
    <IconTooltip id="close-tts" title="Close Text-to-Speech Controls">
      <IconButton
        aria-label="Close Text-to-Speech Controls"
        className={clsx(classes.ttsIconButton, classes.closeBtn)}
        onClick={onClose}
      >
        <Close />
      </IconButton>
    </IconTooltip>
  );
};

const VolumeControl = ({
  classes,
  currentVolume,
  onVolumeChange,
  volumeDownButtonRef,
}) => {
  const volumeSliderValue = Math.round(parseFloat(currentVolume) * 10);

  const [newVolumeSliderValue, setNewVolumeSliderValue] = useState(null);
  const [hiddenVolumeTooltip, setHiddenVolumeTooltip] = useState(null);

  useEffect(() => {
    setHiddenVolumeTooltip(null);

    if (newVolumeSliderValue) {
      let hiddenValue;
      if (newVolumeSliderValue === 0) {
        hiddenValue = 'Volume Mute';
      } else if (newVolumeSliderValue === 10) {
        hiddenValue = 'Volume Max';
      } else {
        hiddenValue = (
          <>
            Volume changed to{' '}
            <span className="noTranslate">{newVolumeSliderValue * 10}%</span>
          </>
        );
      }

      setHiddenVolumeTooltip(hiddenValue);
    }
  }, [newVolumeSliderValue]);

  return (
    <div className={classes.ttscontrols__volume}>
      <div
        id="hiddenVolumeTooltip"
        aria-atomic="true"
        aria-live="assertive"
        className={classes.hiddenSliderTooltipContainer}
      >
        {hiddenVolumeTooltip}
      </div>
      <IconTooltip
        disabled={volumeSliderValue === 0}
        id="decrease-volume"
        title="Decrease Volume"
      >
        <IconButton
          aria-label="Decrease Volume"
          className={classes.ttsIconButton}
          disabled={volumeSliderValue === 0}
          tabIndex={0}
          onClick={() => {
            const newVolume = volumeSliderValue - 1;
            onVolumeChange(newVolume / 10);
            setNewVolumeSliderValue(newVolume);
          }}
          ref={volumeDownButtonRef}
        >
          <VolumeDown />
        </IconButton>
      </IconTooltip>
      <VolumeSlider
        aria-label="volume"
        aria-valuetext={`${volumeSliderValue * 10}% volume`}
        defaultValue={10}
        marks
        max={10}
        min={0}
        onChange={(_, value) => {
          const newVolume = parseInt(value);
          onVolumeChange(newVolume / 10);
          setNewVolumeSliderValue(newVolume);
        }}
        value={volumeSliderValue}
        valueLabelDisplay="auto"
      />
      <IconTooltip
        disabled={volumeSliderValue === 10}
        id="increase-volume"
        title="Increase Volume"
      >
        <IconButton
          aria-label="Increase Volume"
          className={classes.ttsIconButton}
          disabled={volumeSliderValue === 10}
          onClick={() => {
            const newVolume = volumeSliderValue + 1;
            onVolumeChange(newVolume / 10);
            setNewVolumeSliderValue(newVolume);
          }}
        >
          <VolumeUp />
        </IconButton>
      </IconTooltip>
    </div>
  );
};

const PlayControls = ({
  classes,
  setIsPlaying,
  isPlaying,
  isPaused,
  onPause,
  onPlay,
  onRewind,
  onForward,
  hasPrevious,
  hasNext,
  canRead,
  setIsTTSDialogOpen,
  shouldShowTTSDialog,
  handleStartTTS,
}) => {
  const analytics = useContext(AnalyticsModifiersContext);
  const playLabel = isPlaying ? 'Pause' : 'Play';
  const isPlayingOrPaused = isPlaying || isPaused;

  return (
    <div className={classes.ttscontrols__reproduction}>
      <IconTooltip
        disabled={!hasPrevious || !canRead || !isPlayingOrPaused}
        id="rewind"
        title="Rewind 10 Words"
      >
        <IconButton
          aria-label="Rewind 10 Words"
          className={classes.ttsIconButton}
          disabled={!hasPrevious || !canRead || !isPlayingOrPaused}
          onClick={onRewind}
        >
          <Replay10 />
        </IconButton>
      </IconTooltip>
      <IconTooltip disabled={!canRead} id="play-tts" title={playLabel}>
        <IconButton
          aria-label={playLabel}
          className={clsx(classes.ttsIconButton, classes.playBtn)}
          disabled={!canRead}
          onClick={() => {
            if (isPlaying) {
              setIsPlaying(false);
              onPause();
            } else {
              if (shouldShowTTSDialog) {
                setIsTTSDialogOpen(true);
              } else {
                handleStartTTS(() => {
                  setIsPlaying(true);
                  onPlay();
                  analytics.TTSStarted({ TTSLocation: 'TTS bar' });
                });
              }
            }
          }}
        >
          {isPlaying ? (
            <PauseCircleOutline className={classes.playBtnIcon} />
          ) : (
            <PlayCircleOutline className={classes.playBtnIcon} />
          )}
        </IconButton>
      </IconTooltip>
      <IconTooltip
        disabled={!hasNext || !canRead || !isPlayingOrPaused}
        id="forward"
        title="Forward 10 Words"
      >
        <IconButton
          aria-label="Forward 10 Words"
          className={classes.ttsIconButton}
          disabled={!hasNext || !canRead || !isPlayingOrPaused}
          onClick={onForward}
        >
          <Forward10 />
        </IconButton>
      </IconTooltip>
    </div>
  );
};

const TTSSettings = ({
  voices,
  currentVoice,
  onVoiceChange,
  currentRate,
  onRateChange,
  handleClose,
  shouldUseAccordionView,
  isReadyForTooltip,
  closeButtonRef,
  ttsZoomPreference,
  onTTSZoomPreferenceChange,
  ...props
}) => {
  const classes = useStyles();
  const { currentBookLocationDocument } = useContext(
    BookLocationDocumentValuesContext,
  );
  const lastFocusableRef = useRef();

  const handleLastElementKeyDown = (event) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      closeButtonRef.current?.focus();
    }
  };

  const handleFirstElementKeyDown = (event) => {
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      lastFocusableRef.current?.focus();
    }
  };

  return (
    <div className={classes.voiceControls} {...props}>
      <div className={classes.titleContainer}>
        <Typography
          className={classes.title}
          id="tts-title"
          component="h6"
          variant="h6"
        >
          Settings
        </Typography>
        <IconButton
          aria-label="Close"
          className={classes.ttsIconButton}
          ref={closeButtonRef}
          onClick={handleClose}
          onKeyDown={handleFirstElementKeyDown}
        >
          <Close />
        </IconButton>
      </div>
      {shouldUseAccordionView && <Divider />}
      <div className={classes.controls}>
        <TTSSpeed
          currentRate={currentRate}
          onRateChange={onRateChange}
          isReadyForTooltip={isReadyForTooltip}
        />

        {currentBookLocationDocument?.isFixedWidth && (
          <TTSAutoFitControl
            ttsZoomPreference={ttsZoomPreference}
            onTTSZoomPreferenceChange={onTTSZoomPreferenceChange}
          />
        )}

        <TTSVoiceControl
          currentVoice={currentVoice}
          voices={voices}
          onVoiceChange={onVoiceChange}
          lastFocusableRef={lastFocusableRef}
          onLastElementKeyDown={handleLastElementKeyDown}
        />
      </div>
    </div>
  );
};

const VoiceControls = ({
  anchorEl,
  handleClose,
  onVoiceChange,
  currentVoice,
  voices,
  currentRate,
  onRateChange,
  shouldUseAccordionView,
  ttsZoomPreference,
  onTTSZoomPreferenceChange,
}) => {
  const open = Boolean(anchorEl);
  const id = open ? 'tts-settings' : undefined;
  // using this state to make sure the speed tooltip has correct
  // anchor position
  const [isPopoverDone, setIsPopoverDone] = useState(false);
  const closeButtonRef = useRef();

  // Focus the Close button when the popover/drawer opens
  useEffect(() => {
    if (!open) {
      setIsPopoverDone(false);
      return;
    }
  }, [open]);

  // Separate effect for drawer focus with a longer delay
  useEffect(() => {
    if (open && shouldUseAccordionView) {
      const timeoutId = setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 300); // Increased delay for drawer animation

      return () => clearTimeout(timeoutId);
    }
  }, [open, shouldUseAccordionView]);

  if (!open) {
    return null;
  }

  return shouldUseAccordionView ? (
    <Drawer
      aria-labelledby="tts-title"
      role="dialog"
      anchor="bottom"
      open={open}
      SlideProps={{
        onEntered: () => {
          closeButtonRef.current?.focus();
        },
      }}
    >
      <TTSSettings
        voices={voices}
        currentVoice={currentVoice}
        onVoiceChange={onVoiceChange}
        currentRate={currentRate}
        onRateChange={onRateChange}
        handleClose={handleClose}
        isReadyForTooltip={open}
        closeButtonRef={closeButtonRef}
        ttsZoomPreference={ttsZoomPreference}
        onTTSZoomPreferenceChange={onTTSZoomPreferenceChange}
      />
    </Drawer>
  ) : (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      role="dialog"
      aria-labelledby="tts-title"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      TransitionProps={{
        onEntered: () => {
          setIsPopoverDone(true);
          closeButtonRef.current?.focus();
        },
      }}
      disablePortal
    >
      <TTSSettings
        voices={voices}
        currentVoice={currentVoice}
        onVoiceChange={onVoiceChange}
        currentRate={currentRate}
        onRateChange={onRateChange}
        handleClose={handleClose}
        shouldUseAccordionView={shouldUseAccordionView}
        isReadyForTooltip={isPopoverDone}
        closeButtonRef={closeButtonRef}
        ttsZoomPreference={ttsZoomPreference}
        onTTSZoomPreferenceChange={onTTSZoomPreferenceChange}
      />
    </Popover>
  );
};

const OpenSettingsButton = ({ classes, handleClick }) => {
  return (
    <IconTooltip id="tts-settings" shouldUseButtonWidth title="Settings">
      <IconButton
        aria-label="Settings"
        className={clsx(classes.ttsIconButton, classes.ttsSettingsButton)}
        onClick={handleClick}
      >
        <Settings />
      </IconButton>
    </IconTooltip>
  );
};

const TTSControls = ({
  currentVolume = TEXT_TO_SPEECH.DEFAULTS.VOLUME,
  currentVoice = '',
  currentRate = TEXT_TO_SPEECH.DEFAULTS.RATE,
  voices = [],
  isPlaying: isPlayingProp = false,
  isPaused,
  onPlay = () => {},
  onPause = () => {},
  onRewind = () => {},
  onForward = () => {},
  hasPrevious = false,
  hasNext = false,
  canRead = false,
  onVoiceChange = () => {},
  onRateChange = () => {},
  onVolumeChange = () => {},
  onClose = () => {},
  ttsZoomPreference = null,
  onTTSZoomPreferenceChange = () => {},
  handleAccordionClick = () => {},
  isAccordionOpen = false,
  visible = false,
  setIsTTSDialogOpen = () => {},
  shouldShowTTSDialog,
  handleStartTTS = () => {},
  ...otherProps
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles({ visible, isTTSSettingsOpen: Boolean(anchorEl) });

  const { viewport } = useContext(ViewportValuesContext);
  const shouldUseAccordionView =
    viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const [isPlaying, setIsPlaying] = useState();
  const volumeDownButtonRef = useRef();
  const accordionButtonRef = useRef();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e) => {
    if (e.key !== 'Escape') {
      setAnchorEl(null);
    }
  };

  // Send focus to 'volume down' button when TTS bar opens on Desktop
  // or send focus to accordion button when TTS bar opens on Mobile
  useEffect(() => {
    if (visible) {
      if (shouldUseAccordionView && accordionButtonRef.current) {
        accordionButtonRef.current.focus();
      } else if (volumeDownButtonRef.current) {
        volumeDownButtonRef.current.focus();
      }
    }
  }, [visible, shouldUseAccordionView]);

  useEffect(() => {
    setIsPlaying(isPlayingProp);
  }, [isPlayingProp]);

  const handleEscOnTTSControls = (e) => {
    if (e.key === 'Escape' && Boolean(anchorEl)) {
      setAnchorEl(null);
      return;
    }

    if (e.key === 'Escape' && visible) {
      if (isPlaying) {
        onPause();
      } else {
        onClose();
      }
    }
  };

  const TextToSpeechLabel = `${
    isAccordionOpen ? 'Collapse' : 'Expand'
  } Text-to-Speech Controls`;

  if (shouldUseAccordionView) {
    return (
      <div
        className={classes.mobileTTScontrols}
        id="tts-controls-menu"
        onKeyUp={handleEscOnTTSControls}
      >
        <div className={classes.mobileAccordion}>
          <IconTooltip id="tts-accordion" title={TextToSpeechLabel}>
            <IconButton
              aria-controls="tts-accordion-controls"
              aria-expanded={isAccordionOpen}
              aria-label={TextToSpeechLabel}
              className={clsx(
                classes.ttsIconButton,
                classes.mobileAccordionButton,
              )}
              onClick={handleAccordionClick}
              ref={accordionButtonRef}
            >
              {isAccordionOpen ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          </IconTooltip>
          <PlayControls
            classes={classes}
            onForward={onForward}
            onRewind={onRewind}
            onPause={onPause}
            onPlay={onPlay}
            setIsPlaying={setIsPlaying}
            isPaused={isPaused}
            isPlaying={isPlaying}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            canRead={canRead}
            setIsTTSDialogOpen={setIsTTSDialogOpen}
            shouldShowTTSDialog={shouldShowTTSDialog}
            handleStartTTS={handleStartTTS}
          />
          <CloseButton classes={classes} onClose={onClose} />
        </div>
        {isAccordionOpen && (
          <div
            id="tts-accordion-controls"
            className={classes.accordionContents}
          >
            <VolumeControl
              classes={classes}
              currentVolume={currentVolume}
              onVolumeChange={onVolumeChange}
              volumeDownButtonRef={volumeDownButtonRef}
            />
            <OpenSettingsButton classes={classes} handleClick={handleClick} />
            <VoiceControls
              anchorEl={anchorEl}
              handleClose={handleClose}
              onVoiceChange={onVoiceChange}
              currentVoice={currentVoice}
              voices={voices}
              currentRate={currentRate}
              onRateChange={onRateChange}
              shouldUseAccordionView={shouldUseAccordionView}
              ttsZoomPreference={ttsZoomPreference}
              onTTSZoomPreferenceChange={onTTSZoomPreferenceChange}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={classes.ttscontrols}
      id="tts-controls-menu"
      onKeyUp={handleEscOnTTSControls}
      {...otherProps}
    >
      <div className={classes.controlsContainer}>
        <VolumeControl
          classes={classes}
          currentVolume={currentVolume}
          onVolumeChange={onVolumeChange}
          volumeDownButtonRef={volumeDownButtonRef}
        />
        <PlayControls
          classes={classes}
          onForward={onForward}
          onRewind={onRewind}
          onPause={onPause}
          onPlay={onPlay}
          setIsPlaying={setIsPlaying}
          isPaused={isPaused}
          isPlaying={isPlaying}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          canRead={canRead}
          setIsTTSDialogOpen={setIsTTSDialogOpen}
          shouldShowTTSDialog={shouldShowTTSDialog}
          handleStartTTS={handleStartTTS}
        />
        <div className={classes.ttsSettingIconContainer}>
          <OpenSettingsButton classes={classes} handleClick={handleClick} />
        </div>
        <VoiceControls
          anchorEl={anchorEl}
          handleClose={handleClose}
          onVoiceChange={onVoiceChange}
          currentVoice={currentVoice}
          voices={voices}
          currentRate={currentRate}
          onRateChange={onRateChange}
          ttsZoomPreference={ttsZoomPreference}
          onTTSZoomPreferenceChange={onTTSZoomPreferenceChange}
        />
      </div>
      <div className={classes.controlsCloseButtonContainer}>
        <CloseButton classes={classes} onClose={onClose} />
      </div>
    </div>
  );
};

export default TTSControls;
